import { Formio } from '@formio/angular';
import type Field from '@formio/js/lib/cjs/components/_classes/field/Field';

const COLORS = {
  active: 'var(--Vetai-Inline_links_and_info--Default)',
  track: 'var(--Vetai-Form_controls--Border_Default)',
  thumbBg: 'var(--Vetai-Absolute_packages--White--Default)',
  tickInactive: 'var(--Vetai-Absolute_packages--White--Default)',
  tickInactiveBorder: 'var(--Vetai-Form_controls--Border_Default)',
} as const;

/** DOM refs created by this component (Form.io `refs` is typed as `{}`). */
type StepSliderRefs = {
  trackArea?: HTMLElement;
  thumb?: HTMLElement;
  trackActive?: HTMLElement;
  input?: HTMLInputElement[];
};

/**
 * Discrete step slider (Pawprint / Figma "Continuous Marked" style).
 * Submission value is the displayed step number (stepStart … stepStart + numSteps − 1), or empty
 * when the field has no selection and no default (leave Default Value empty in the builder).
 *
 * Call once after `Formio.use(premium)` (same pattern as other custom Formio modules).
 *
 * Form.io hosted builder: register the same component via Project → Custom JavaScript using the
 * deployed URL to `assets/formio/formio-step-slider.js` (keep that file in sync when changing this module).
 */
export function registerFormioStepSlider(): void {
  const FieldBase = Formio.Components.components.field as typeof Field | undefined;
  if (!FieldBase) return;
  const Base = FieldBase;

  class StepSliderComponent extends Base {
    static override schema(...extend: unknown[]) {
      return Base.schema(
        {
          type: 'stepslider',
          label: 'Step slider',
          key: 'stepSlider',
          input: true,
          tableView: true,
          numSteps: 9,
          leftLabel: '',
          rightLabel: '',
          showSliderSteps: true,
          showStepLabels: false,
          stepStart: 0,
        },
        ...extend
      );
    }

    static editForm = (...extend: unknown[]) =>
      Formio.Components.baseEditForm(
        [
          {
            key: 'display',
            components: [
              {
                weight: -50,
                type: 'number',
                input: true,
                key: 'numSteps',
                label: 'Number of steps',
                tooltip: 'How many discrete positions appear on the track.',
                validate: { min: 2, max: 100, step: 1 },
              },
              {
                weight: -45,
                type: 'number',
                input: true,
                key: 'stepStart',
                label: 'Starting step number',
                tooltip: 'The number shown on the first step. Steps increment by 1 from this value.',
                validate: { step: 1 },
              },
              {
                weight: -40,
                type: 'textfield',
                input: true,
                key: 'leftLabel',
                label: 'Left label',
              },
              {
                weight: -39,
                type: 'textfield',
                input: true,
                key: 'rightLabel',
                label: 'Right label',
              },
              {
                weight: -38,
                type: 'checkbox',
                input: true,
                key: 'showSliderSteps',
                label: 'Show slider steps',
                tooltip: 'When checked, shows a dot on the track for each discrete position.',
              },
              {
                weight: -37,
                type: 'checkbox',
                input: true,
                key: 'showStepLabels',
                label: 'Show step labels',
                tooltip: 'When checked, shows the step number under each position.',
              },
            ],
          },
        ],
        ...extend
      );

    static get builderInfo() {
      return {
        title: 'Step slider',
        group: 'basic',
        icon: 'sliders',
        weight: 65,
        documentation: 'https://help.form.io/developers/form-development/custom-components',
        schema: StepSliderComponent.schema(),
      };
    }

    get numSteps(): number {
      const n = parseInt(String(this.component.numSteps ?? 9), 10);
      return Number.isFinite(n) && n >= 2 ? n : 9;
    }

    get showSliderSteps(): boolean {
      return this.component.showSliderSteps !== false;
    }

    get showStepLabels(): boolean {
      return !!this.component.showStepLabels;
    }

    get stepStart(): number {
      const s = parseInt(String(this.component.stepStart ?? 0), 10);
      return Number.isFinite(s) ? s : 0;
    }

    override get defaultSchema() {
      return StepSliderComponent.schema();
    }

    /** Display step value, clamped to [stepStart, stepStart + numSteps − 1]. */
    normalizeStep(value: unknown): number | null {
      if (value === '' || value === undefined || value === null) {
        return null;
      }
      const v = typeof value === 'number' ? value : parseInt(String(value), 10);
      if (Number.isNaN(v)) return null;
      const min = this.stepStart;
      const max = min + this.numSteps - 1;
      if (max < min) return null;
      return Math.min(max, Math.max(min, v));
    }

    /** Resolved step for UI; null when there is no stored value and no component default. */
    getCurrentStepForRender(): number | null {
      const fromData = this.normalizeStep(this.dataValue);
      if (fromData != null) return fromData;
      return this.normalizeStep(this.component.defaultValue);
    }

    /** `step` is the displayed step value; maps to thumb position. */
    percentForStep(step: number): number {
      const n = this.numSteps;
      if (n <= 1) return 50;
      return ((step - this.stepStart) / (n - 1)) * 100;
    }

    /** Horizontal center for tick index `i` (0-based), same as `percentForStep(stepStart + i)`. */
    stepIndexCenterPercent(i: number, n: number): number {
      if (n <= 1) return 50;
      return (i / (n - 1)) * 100;
    }

    override render() {
      const refAttr = this._referenceAttributeName || 'ref';
      const step = this.getCurrentStepForRender();
      const n = this.numSteps;
      const pct = step == null ? 0 : this.percentForStep(step);
      const left = this.interpolate(this.component.leftLabel || '', { _userInput: true });
      const right = this.interpolate(this.component.rightLabel || '', { _userInput: true });

      const stepIdx = step == null ? -1 : step - this.stepStart;
      const showTicks = this.showSliderSteps;
      const ticks: string[] = [];
      if (showTicks) {
        for (let i = 0; i < n; i++) {
          const filled = i <= stepIdx;
          const bg = filled ? COLORS.active : COLORS.tickInactive;
          const border = filled ? COLORS.active : COLORS.tickInactiveBorder;
          const cx = this.stepIndexCenterPercent(i, n);
          ticks.push(
            `<span class="formio-step-slider__tick" style="position:absolute;left:${cx}%;top:50%;transform:translate(-50%,-50%);width:8px;height:8px;border-radius:50%;box-sizing:border-box;background:${bg};border:1px solid ${border};pointer-events:none;"></span>`
          );
        }
      }

      const showLabels = this.showStepLabels;
      const labelColor = (i: number) =>
        i <= stepIdx ? COLORS.active : 'var(--Vetai-Form_controls--Text_Default, #152138)';
      const stepLabelsHtml = showLabels
        ? Array.from({ length: n }, (_, i) => {
            const num = this.stepStart + i;
            const color = labelColor(i);
            const cx = this.stepIndexCenterPercent(i, n);
            return `<span class="formio-step-slider__step-label" data-step-index="${i}" style="position:absolute;left:${cx}%;transform:translateX(-50%);font-size:12px;line-height:16px;font-weight:500;color:${color};white-space:nowrap;">${num}</span>`;
          }).join('')
        : '';

      const disabled = this.disabled ? 'disabled aria-disabled="true"' : '';
      const hiddenVal = step == null ? '' : String(step);
      const thumbAriaValue = step == null ? 'aria-valuetext="Not selected"' : `aria-valuenow="${step}"`;
      const trackAreaMinH = showLabels ? '48px' : '32px';

      const html = `
<div class="formio-step-slider" style="width:100%;box-sizing:border-box;">
  <input type="hidden" class="formio-step-slider__hidden" ${refAttr}="input" value="${hiddenVal}" />
  <div style="display:flex;align-items:center;gap:16px;width:100%;">
    <span class="formio-step-slider__label-left" style="flex:1 1 0;min-width:0;max-width:40%;white-space:normal;overflow-wrap:break-word;word-break:normal;font-size:var(--Vetai-T5, 16px);line-height:var(--Vetai-24, 24px);color:var(--Vetai-Form_controls--Text_Default, #152138);">${left}</span>
    <div ${refAttr}="trackArea" class="formio-step-slider__track-area" style="flex:1;position:relative;min-height:${trackAreaMinH};min-width:80px;touch-action:none;cursor:pointer;">
      <div style="position:relative;height:32px;width:100%;">
      <div ${refAttr}="trackBg" style="position:absolute;left:0;right:0;top:50%;transform:translateY(-50%);height:4px;background:${COLORS.track};border-radius:4px;"></div>
      <div ${refAttr}="trackActive" style="position:absolute;left:0;top:50%;transform:translateY(-50%);height:2px;background:${COLORS.active};border-radius:4px;width:${pct}%;pointer-events:none;"></div>
      <div ${refAttr}="ticksRow" style="position:absolute;left:0;right:0;top:0;bottom:0;pointer-events:none;">
        ${ticks.join('')}
      </div>
      <button type="button" ${disabled}
        ${refAttr}="thumb"
        role="slider"
        aria-valuemin="${this.stepStart}"
        aria-valuemax="${this.stepStart + n - 1}"
        ${thumbAriaValue}
        aria-label="${this.component.label || 'Step slider'}"
        style="position:absolute;top:50%;left:calc(${pct}% - 8px);width:16px;height:16px;padding:0;border:none;border-radius:50%;background:${COLORS.thumbBg};box-shadow:0 0 4px 0 rgba(21,33,56,0.2);cursor:${this.disabled ? 'not-allowed' : 'grab'};transform:translateY(-50%);z-index:2;"
      ></button>
      </div>
      ${showLabels ? `<div ${refAttr}="stepLabelsRow" class="formio-step-slider__step-labels" style="position:relative;height:16px;width:100%;box-sizing:border-box;padding-top:4px;"><div style="position:relative;width:100%;height:100%;">${stepLabelsHtml}</div></div>` : ''}
    </div>
    <span class="formio-step-slider__label-right" style="flex:1 1 0;min-width:0;max-width:40%;white-space:normal;overflow-wrap:break-word;word-break:normal;text-align:right;font-size:var(--Vetai-T5, 16px);line-height:var(--Vetai-24, 24px);color:var(--Vetai-Form_controls--Text_Default, #152138);">${right}</span>
  </div>
</div>`;
      return super.render(html as unknown as object);
    }

    override attach(element: HTMLElement) {
      return super.attach(element).then(() => {
        this.loadRefs(element, {
          input: 'multiple',
          trackArea: 'single',
          trackActive: 'single',
          thumb: 'single',
          ticksRow: 'single',
        });

        const refs = this.refs as StepSliderRefs;
        const trackArea = refs.trackArea;
        const thumb = refs.thumb;
        const trackActive = refs.trackActive;
        const inputs = refs.input;

        const applyVisual = (step: number | null) => {
          const pct = step == null ? 0 : this.percentForStep(step);
          const stepIdx = step == null ? -1 : step - this.stepStart;
          if (trackActive) trackActive.style.width = `${pct}%`;
          if (thumb) {
            thumb.style.left = `calc(${pct}% - 8px)`;
            if (step == null) {
              thumb.removeAttribute('aria-valuenow');
              thumb.setAttribute('aria-valuetext', 'Not selected');
            } else {
              thumb.removeAttribute('aria-valuetext');
              thumb.setAttribute('aria-valuenow', String(step));
            }
          }
          if (this.showSliderSteps) {
            const tickEls = trackArea?.querySelectorAll('.formio-step-slider__tick');
            tickEls?.forEach((el, i) => {
              const filled = i <= stepIdx;
              const h = el as HTMLElement;
              h.style.background = filled ? COLORS.active : COLORS.tickInactive;
              h.style.borderColor = filled ? COLORS.active : COLORS.tickInactiveBorder;
            });
          }
          if (this.showStepLabels) {
            trackArea?.querySelectorAll?.('.formio-step-slider__step-label').forEach((el, i) => {
              const h = el as HTMLElement;
              const filled = i <= stepIdx;
              h.style.color = filled ? COLORS.active : 'var(--Vetai-Form_controls--Text_Default, #152138)';
            });
          }
          if (inputs?.[0]) inputs[0].value = step == null ? '' : String(step);
        };

        const stepFromClientX = (clientX: number): number => {
          if (!trackArea) return this.stepStart;
          const rect = trackArea.getBoundingClientRect();
          const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
          return this.stepStart + Math.round(ratio * (this.numSteps - 1));
        };

        const commitStep = (step: number, flags: Record<string, unknown> = {}) => {
          if (this.disabled) return;
          const changed = this.updateValue(step, { ...flags, modified: true });
          if (changed) {
            applyVisual(step);
          }
        };

        applyVisual(this.normalizeStep(this.dataValue) ?? this.getCurrentStepForRender());

        if (!trackArea || !thumb || this.disabled) {
          return;
        }

        let dragging = false;

        const onPointerDown = (e: PointerEvent) => {
          if (this.disabled) return;
          dragging = true;
          thumb.setPointerCapture(e.pointerId);
          thumb.style.cursor = 'grabbing';
        };

        const onPointerMove = (e: PointerEvent) => {
          if (!dragging || this.disabled) return;
          commitStep(stepFromClientX(e.clientX), { modified: true });
        };

        const onPointerUp = (e: PointerEvent) => {
          if (dragging) {
            dragging = false;
            try {
              thumb.releasePointerCapture(e.pointerId);
            } catch {
              /* ignore */
            }
            thumb.style.cursor = 'grab';
          }
        };

        const onTrackClick = (e: MouseEvent) => {
          if (this.disabled || (e.target as HTMLElement)?.closest?.('button')) return;
          commitStep(stepFromClientX(e.clientX), { modified: true });
        };

        const onKeyDown = (e: KeyboardEvent) => {
          if (this.disabled) return;
          const cur = this.normalizeStep(this.dataValue);
          const maxStep = this.stepStart + this.numSteps - 1;
          if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
            e.preventDefault();
            if (cur == null) {
              commitStep(maxStep, { modified: true });
            } else {
              commitStep(Math.max(this.stepStart, cur - 1), { modified: true });
            }
          } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (cur == null) {
              commitStep(this.stepStart, { modified: true });
            } else {
              commitStep(Math.min(maxStep, cur + 1), { modified: true });
            }
          } else if (e.key === 'Home') {
            e.preventDefault();
            commitStep(this.stepStart, { modified: true });
          } else if (e.key === 'End') {
            e.preventDefault();
            commitStep(maxStep, { modified: true });
          }
        };

        this.addEventListener(thumb, 'pointerdown', onPointerDown, false, false);
        this.addEventListener(thumb, 'pointermove', onPointerMove, false, false);
        this.addEventListener(thumb, 'pointerup', onPointerUp, false, false);
        this.addEventListener(thumb, 'pointercancel', onPointerUp, false, false);
        this.addEventListener(trackArea, 'click', onTrackClick, false, false);
        this.addEventListener(thumb, 'keydown', onKeyDown, false, false);
      });
    }

    override normalizeValue(value: unknown) {
      const n = this.normalizeStep(value);
      return n == null ? null : n;
    }

    override getValueAt(index: number) {
      const raw = super.getValueAt(index);
      if (raw === '' || raw == null) return null;
      const n = parseInt(String(raw), 10);
      return Number.isNaN(n) ? null : n;
    }

    override setValueAt(index: number, value: unknown, flags = {}) {
      const n = this.normalizeStep(value);
      const r = super.setValueAt(index, n == null ? '' : String(n), flags);
      const step = this.normalizeStep(this.dataValue) ?? this.normalizeStep(this.component.defaultValue);
      const stepIdx = step == null ? -1 : step - this.stepStart;
      const rfs = this.refs as StepSliderRefs;
      const trackActive = rfs.trackActive;
      const thumb = rfs.thumb;
      const ta = rfs.trackArea;
      const pct = step == null ? 0 : this.percentForStep(step);
      if (trackActive) trackActive.style.width = `${pct}%`;
      if (thumb) {
        thumb.style.left = `calc(${pct}% - 8px)`;
        if (step == null) {
          thumb.removeAttribute('aria-valuenow');
          thumb.setAttribute('aria-valuetext', 'Not selected');
        } else {
          thumb.removeAttribute('aria-valuetext');
          thumb.setAttribute('aria-valuenow', String(step));
        }
      }
      if (this.showSliderSteps) {
        ta?.querySelectorAll?.('.formio-step-slider__tick').forEach((el, i) => {
          const filled = i <= stepIdx;
          const h = el as HTMLElement;
          h.style.background = filled ? COLORS.active : COLORS.tickInactive;
          h.style.borderColor = filled ? COLORS.active : COLORS.tickInactiveBorder;
        });
      }
      ta?.querySelectorAll?.('.formio-step-slider__step-label').forEach((el, i) => {
        const filled = i <= stepIdx;
        const h = el as HTMLElement;
        h.style.color = filled ? COLORS.active : 'var(--Vetai-Form_controls--Text_Default, #152138)';
      });
      const inputs = rfs.input;
      if (inputs?.[0]) inputs[0].value = step == null ? '' : String(step);
      return r;
    }

    override getView(value: unknown) {
      if (value === null || value === undefined || value === '') return '';
      return String(value);
    }
  }

  Formio.Components.addComponent('stepslider', StepSliderComponent);
}
